import {
    addDoc,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    updateDoc,
    where,
    writeBatch
} from 'firebase/firestore';
import { productCategoriesRef, ProductCategory } from '@api/ProductCategories/ProductCategory';
import { firestore } from '@api/Firebase/Firebase';
import { buildCreationStamp } from '@api/ActivityLogs/CreationStamp';
import { logCatalogChange, PRODUCT_CATEGORY_FIELDS } from '@api/ActivityLogs/CatalogActivity';
import { publishCatalog } from '@api/Catalog/CatalogVersion';

export default {
    async saveProductCategory(productCategory: ProductCategory): Promise<ProductCategory> {
        const ProductCategoryExist = await this.getProductCategoryByName(productCategory.name);
        if (ProductCategoryExist) {
            throw new Error('Categoría de menu existente.');
        }
        const isNew = !productCategory.id;
        const before = isNew ? null : await this.getProductCategory(productCategory.id as string);
        if (isNew) {
            const data = await addDoc(productCategoriesRef, {
                ...productCategory,
                ...(await buildCreationStamp())
            });
            productCategory.id = data.id;
        }
        await updateDoc(doc(productCategoriesRef, productCategory.id), { ...productCategory });
        await publishCatalog('product-category');
        await logCatalogChange('product-category', productCategory.id, before, productCategory, PRODUCT_CATEGORY_FIELDS);
        return productCategory;
    },
    /**
     * Reordena el menú. **Cada categoría que se mueve deja su propio renglón de
     * bitácora** (M2): el 2026-09-10 el menú salió desordenado en producción y no
     * hubo forma de saber quién lo había movido.
     *
     * Va por categoría y no como un evento global a propósito (D5): la bitácora
     * se abre desde el objeto, así que un renglón que no cuelgue de una categoría
     * no tendría dónde verse. Cuesta 1 lectura de la colección (8 documentos) y
     * una escritura por categoría MOVIDA, en una acción manual y poco frecuente.
     */
    async updateProductCategoriesOrder(productCategories: ProductCategory[]): Promise<void> {
        const previas = new Map((await this.getProductCategories()).map((category) => [category.id, category]));
        const batch = writeBatch(firestore);
        productCategories.forEach((category) => {
            const docRef = doc(productCategoriesRef, category.id);
            batch.update(docRef, { priority: category.priority });
        });
        await batch.commit();
        await publishCatalog('product-category-order');
        await Promise.all(
            productCategories.map((category) =>
                logCatalogChange(
                    'product-category',
                    category.id,
                    previas.get(category.id),
                    category,
                    PRODUCT_CATEGORY_FIELDS
                )
            )
        );
        return;
    },
    async getProductCategories(): Promise<ProductCategory[]> {
        const productCategories: ProductCategory[] = [];
        const queryFilters = [];
        queryFilters.push(orderBy('priority', 'asc'));
        const queryDoc = query(productCategoriesRef, ...queryFilters);
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return productCategories;
        }
        snapshot.docs.map((doc: any) => {
            productCategories.push(doc.data() as ProductCategory);
        });
        return productCategories;
    },
    async getProductCategory(productCategoryId: string): Promise<ProductCategory> {
        const documentData = await getDoc(doc(productCategoriesRef, productCategoryId));
        return documentData.data() as ProductCategory;
    },
    async getProductCategoryByName(name: string): Promise<ProductCategory | undefined> {
        let productCategory = undefined;
        const queryProductCategory = query(productCategoriesRef, where('name', '==', name));
        const snapshot = await getDocs(queryProductCategory);
        if (snapshot.empty) {
            return productCategory;
        }
        snapshot.forEach((doc: any) => {
            productCategory = doc.data() as ProductCategory;
        });
        return productCategory;
    },
    async deleteProductCategory(productCategoryId: string): Promise<void> {
        await deleteDoc(doc(productCategoriesRef, productCategoryId));
        await publishCatalog('product-category-delete');
        return;
    }
};
